
import { JSX } from 'react';
import { PlayerOverview } from '../../../types/player';
import { getFormIcon, getPlayingTimeIcon } from '../../../utils/iconUtils';
import { AlertTableExcludeCell, AlertTableExcludedCell, IndexCell, PlayerTableAgeCell, PlayerTableContractCell, PositionCell } from '../tableCells';


interface AlertPlayerTableColumnCellProps {
  row: PlayerOverview;
  language?: string;
  positionGroup?: string;
  excludePlayer?: (playerId: number, allAlerts: boolean, monthsExcluded: number) => void;
  isShowAllExcludedPlayersToggled?: boolean;
  undoExcludePlayer?: (playerOverview: PlayerOverview) => void;
}


interface AlertPlayerTableColumn {
  key: string;
  width: number;

  orderByKey?: string;
  roleId?: string;

  cell: (props: AlertPlayerTableColumnCellProps) => JSX.Element;
}

const alertTableInfoColumnsWidth = 270;
const alertTableStaticOverallDataColumnsWidth = 240;
const alertTableActionColumnWidth = 150;

export const alertTablePadding = 15;
export const alertTableStaticColumnsWidth = alertTableInfoColumnsWidth + alertTableStaticOverallDataColumnsWidth + alertTableActionColumnWidth + alertTablePadding;
export const alertTableOverallIndexColumnWidth = 100;
export const alertTableRoleIndexColumnWidth = 110;

export const alertTableInfoColumns: AlertPlayerTableColumn[] = [
  {
    key: 'age',
    width: 90,
    orderByKey: 'age',
    cell: ({ row, language }: AlertPlayerTableColumnCellProps) => (
      <PlayerTableAgeCell row={row} language={language} />
    ),
  },
  {
    key: 'contract',
    width: 90,
    cell: ({ row, language }: AlertPlayerTableColumnCellProps) => (
      <PlayerTableContractCell row={row} language={language} />
    ),
  },
  {
    key: 'position',
    width: 90,
    cell: ({ row, language }: AlertPlayerTableColumnCellProps) => (
      <PositionCell row={row} language={language} maxPositions={2} />
    ),
  },
];


export const getAlertTableOverallDataColumns = (
  hasSkillcorner: boolean,
  showIndexConfidences: boolean,
) => {

  const dataColumns: AlertPlayerTableColumn[] = [
    {
      key: 'form',
      width: 60,
      cell: ({ row }: AlertPlayerTableColumnCellProps) => (
        <div className='table-cell'>
          {getFormIcon(row.form_status, 18, 'dark', 43)}
        </div>
      ),
    },
    {
      key: 'playingTime',
      width: 80,
      cell: ({ row }: AlertPlayerTableColumnCellProps) => (
        <div className='table-cell'>
          {getPlayingTimeIcon(row.availability_status, 18, 'dark')}
        </div>
      ),
    },
    {
      key: 'skill_index',
      width: alertTableOverallIndexColumnWidth,
      orderByKey: 'skill_index',
      cell: ({ row, positionGroup }: AlertPlayerTableColumnCellProps) => (
        <IndexCell
          playerOverview={row}
          property='skill_index'
          positionGroup={positionGroup ?? 'OVERALL'}
          showConfidence={showIndexConfidences}
        />
      ),
    },
  ];

  if (hasSkillcorner) {
    dataColumns.push({
      key: 'physical_index',
      width: alertTableOverallIndexColumnWidth,
      orderByKey: 'physical_index',
      cell: ({ row, positionGroup }: AlertPlayerTableColumnCellProps) => (
        <IndexCell
          playerOverview={row}
          property='physical_index'
          positionGroup={positionGroup ?? 'OVERALL'}
          showConfidence={showIndexConfidences}
        />
      ),
    },);
  }

  return dataColumns;
};


export const getAlertTableRoleIndexColumns = (
  selectedRoles: string[] | undefined,
  showIndexConfidences: boolean,
) => {

  const dataColumns: AlertPlayerTableColumn[] = [];

  if (selectedRoles) {
    selectedRoles.forEach(roleId => {
      dataColumns.push({
        key: roleId,
        width: alertTableRoleIndexColumnWidth,
        roleId: roleId,
        orderByKey: roleId,
        cell: ({ row, positionGroup }: AlertPlayerTableColumnCellProps) => (
          <IndexCell
            playerOverview={row}
            property={roleId}
            positionGroup={positionGroup ?? 'OVERALL'}
            showConfidence={showIndexConfidences}
          />
        ),
      });
    });
  }

  return dataColumns;
};


export const getAlertTableActionColumn = (isExcludedPlayersView: boolean): AlertPlayerTableColumn => {
  if (isExcludedPlayersView) {
    return {
      key: 'undoAll',
      width: alertTableActionColumnWidth + alertTablePadding,
      cell: ({ row, language, isShowAllExcludedPlayersToggled, undoExcludePlayer }: AlertPlayerTableColumnCellProps) => (
        <div className='table-cell' style={{ paddingRight: alertTablePadding }}>
          <AlertTableExcludedCell
            row={row}
            language={language}
            isShowAllExcludedPlayersToggled={isShowAllExcludedPlayersToggled}
            undoExcludePlayer={undoExcludePlayer}
          />
        </div>
      ),
    };
  }

  return {
    key: 'removeAll',
    width: alertTableActionColumnWidth + alertTablePadding,
    cell: ({ row, language, excludePlayer }: AlertPlayerTableColumnCellProps) => (
      <div className='table-cell' style={{ paddingRight: alertTablePadding }}>
        <AlertTableExcludeCell
          row={row}
          language={language}
          excludePlayer={excludePlayer}
        />
      </div>
    ),
  };
};
